/**
 * ScreeningRunDetail — per-customer outcomes for one screening run.
 *
 * Doubles as the reviewer workspace: filter to potential matches, open a
 * customer's candidate hits, and record a false-positive or confirmed-match
 * decision. Both report formats download from here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import FileSpreadsheet from 'lucide-react/dist/esm/icons/file-spreadsheet';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import { useSanctionsStore } from '../../stores/sanctionsStore';
import { amlApi } from '../../services/amlApi';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import { useToast } from '../../../../components/feedback';
import {
  Badge, Button, Card, Pagination, SearchInput, Select, Spinner, Text,
} from '../../../../components/ui';
import { PageSizeSelect, TableEmptyRow, downloadBlob } from '../shared';
import MatchReviewPanel from './MatchReviewPanel';
import type {
  ScreeningResult, ScreeningResultType, ScreeningReviewStatus,
} from '../../types/aml';

const DEBOUNCE_MS = 300;

const RESULT_MAP = {
  cleared: { variant: 'success', labelKey: 'amlScreeningResultCleared' },
  potential_match: { variant: 'danger', labelKey: 'amlScreeningResultPotentialMatch' },
} as const;

const REVIEW_MAP = {
  pending: { variant: 'neutral', labelKey: 'amlScreeningReviewPending' },
  false_positive: { variant: 'info', labelKey: 'amlScreeningReviewFalsePositive' },
  confirmed_match: { variant: 'danger', labelKey: 'amlScreeningReviewConfirmedMatch' },
} as const;

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatDay(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatScore(value: string | null): string {
  if (value == null) return '—';
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? value : parsed.toFixed(1);
}

interface ResultRowProps {
  result: ScreeningResult;
  onOpen: (result: ScreeningResult) => void;
  t: (key: string) => string;
}

const ResultRow = React.memo<ResultRowProps>(({ result, onOpen, t }) => {
  // Algorithm hit stays on the audit row; after a false-positive review the
  // effective status is Cleared so the row no longer looks like an open hit.
  const isOpenHit =
    result.result === 'potential_match' && result.review_status === 'pending';
  const effectiveResult: keyof typeof RESULT_MAP =
    result.review_status === 'false_positive' ? 'cleared' : result.result;
  const resultMeta = RESULT_MAP[effectiveResult];
  const reviewMeta = REVIEW_MAP[result.review_status];
  const topMatch = result.matches[0];
  const canReview = result.result === 'potential_match';

  const handleOpen = useCallback(() => onOpen(result), [onOpen, result]);

  return (
    <tr
      className={`border-b border-subtle transition-colors hover:bg-surface-secondary/50 ${
        isOpenHit ? 'bg-status-error/5 cursor-pointer' : canReview ? 'cursor-pointer bg-surface-primary' : 'bg-surface-primary'
      }`}
      onClick={canReview ? handleOpen : undefined}
    >
      <td className="px-4 py-3 text-sm font-medium text-content-primary">
        {result.customer_name_snapshot}
      </td>
      <td className="px-4 py-3 text-sm text-content-muted">
        {result.customer_address_snapshot || '—'}
      </td>
      <td className="px-4 py-3">
        <Badge variant={resultMeta.variant} size="sm" dot>
          {t(resultMeta.labelKey)}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-content-secondary tabular-nums text-right">
        {formatScore(result.best_score)}
      </td>
      <td className="px-4 py-3 text-sm text-content-secondary">
        {topMatch ? (
          <span>
            {topMatch.matched_name}
            {topMatch.name_type === 'aka' ? (
              <span className="text-content-muted"> ({t('amlScreeningAka')})</span>
            ) : null}
            {result.match_count > 1 ? (
              <span className="text-content-muted">
                {' '}+{result.match_count - 1}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="text-content-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-content-muted">
        {topMatch?.list_type ? t(`amlSanctionsListShort_${topMatch.list_type}`) : '—'}
      </td>
      <td className="px-4 py-3">
        {canReview ? (
          <Badge variant={reviewMeta.variant} size="sm">
            {t(reviewMeta.labelKey)}
          </Badge>
        ) : (
          <span className="text-sm text-content-muted">—</span>
        )}
      </td>
    </tr>
  );
});
ResultRow.displayName = 'ResultRow';

const ScreeningRunDetail: React.FC = () => {
  const {
    activeRun, results, resultsTotal, resultsPage, resultsPageSize, resultsLoading,
    resultFilter, reviewFilter,
    closeRun, fetchResults, setResultFilter, setReviewFilter, setResultsSearch,
    setResultsPageSize,
  } = useSanctionsStore();

  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);
  const { success, error: toastError } = useToast();

  const [localSearch, setLocalSearch] = useState('');
  const [downloading, setDownloading] = useState<'pdf' | 'xlsx' | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ScreeningResult | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  const totalPages = Math.max(1, Math.ceil(resultsTotal / resultsPageSize));
  const startItem = (resultsPage - 1) * resultsPageSize + 1;
  const endItem = Math.min(resultsPage * resultsPageSize, resultsTotal);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    fetchResults(1);
  }, [resultFilter, reviewFilter, resultsPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setResultsSearch(value);
      fetchResults(1);
      debounceRef.current = null;
    }, DEBOUNCE_MS);
  }, [setResultsSearch, fetchResults]);

  const handleDownload = useCallback(async (format: 'pdf' | 'xlsx') => {
    if (!activeRun) return;
    setDownloading(format);
    try {
      const blob = await amlApi.downloadScreeningReport(activeRun.id, format);
      const stamp = (activeRun.finished_at || activeRun.started_at).slice(0, 10);
      const mime = format === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      downloadBlob(new Blob([blob], { type: mime }), `ofac-screening-${stamp}.${format}`);
      success(t('amlScreeningToastReportDownloaded'));
    } catch {
      toastError(t('amlScreeningToastReportFailed'));
    } finally {
      setDownloading(null);
    }
  }, [activeRun, success, toastError, t]);

  const handleDownloadPdf = useCallback(() => handleDownload('pdf'), [handleDownload]);
  const handleDownloadXlsx = useCallback(() => handleDownload('xlsx'), [handleDownload]);

  const resultOptions = useMemo(() => [
    { value: 'all', label: t('amlScreeningFilterAllResults') },
    { value: 'potential_match', label: t('amlScreeningResultPotentialMatch') },
    { value: 'cleared', label: t('amlScreeningResultCleared') },
  ], [t]);

  const reviewOptions = useMemo(() => [
    { value: 'all', label: t('amlScreeningFilterAllReviews') },
    { value: 'pending', label: t('amlScreeningReviewPending') },
    { value: 'false_positive', label: t('amlScreeningReviewFalsePositive') },
    { value: 'confirmed_match', label: t('amlScreeningReviewConfirmedMatch') },
  ], [t]);

  const handleResultFilter = useCallback(
    (value: string) => setResultFilter(value as ScreeningResultType | 'all'),
    [setResultFilter],
  );
  const handleReviewFilter = useCallback(
    (value: string) => setReviewFilter(value as ScreeningReviewStatus | 'all'),
    [setReviewFilter],
  );
  const closeReviewPanel = useCallback(() => setReviewTarget(null), []);

  if (!activeRun) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft size={14} />}
            onClick={closeRun}
          >
            {t('amlScreeningBtnBack')}
          </Button>
          <Text variant="heading" className="mt-2 block">
            {t('amlScreeningDetailTitle')}
          </Text>
          <Text variant="caption" className="mt-1 block">
            {formatDateTime(activeRun.finished_at || activeRun.started_at)}
            {' · '}
            {t('amlScreeningThresholdLabel')}: {activeRun.threshold}%
          </Text>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="md"
            leftIcon={<FileText size={14} />}
            loading={downloading === 'pdf'}
            onClick={handleDownloadPdf}
          >
            {t('amlScreeningBtnDownloadPdf')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            leftIcon={<FileSpreadsheet size={14} />}
            loading={downloading === 'xlsx'}
            onClick={handleDownloadXlsx}
          >
            {t('amlScreeningBtnDownloadExcel')}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4">
          <Text variant="label">{t('amlScreeningColScreened')}</Text>
          <Text variant="metric" className="mt-1 block">
            {activeRun.customers_screened.toLocaleString()}
          </Text>
        </Card>
        <Card className="p-4">
          <Text variant="label">{t('amlScreeningColCleared')}</Text>
          <Text variant="metric" className="mt-1 block text-status-success">
            {activeRun.cleared_count.toLocaleString()}
          </Text>
        </Card>
        <Card className="p-4">
          <Text variant="label">{t('amlScreeningColPotentialMatches')}</Text>
          <Text variant="metric" className="mt-1 block text-status-error">
            {activeRun.potential_match_count.toLocaleString()}
          </Text>
        </Card>
        <Card className="p-4">
          <Text variant="label">{t('amlScreeningListVersions')}</Text>
          <Text variant="caption" className="mt-1 block">
            {t('amlSanctionsListShort_SDN')}: {formatDay(activeRun.sdn_published_date)}
          </Text>
          <Text variant="caption" className="block">
            {t('amlSanctionsListShort_NON_SDN')}: {formatDay(activeRun.non_sdn_published_date)}
          </Text>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <SearchInput
          value={localSearch}
          onChange={handleSearchChange}
          placeholder={t('amlScreeningSearchPlaceholder')}
          className="flex-1 max-w-sm"
        />
        <Select
          value={resultFilter}
          onChange={handleResultFilter}
          options={resultOptions}
          className="w-full sm:w-52"
        />
        <Select
          value={reviewFilter}
          onChange={handleReviewFilter}
          options={reviewOptions}
          className="w-full sm:w-52"
        />
      </div>

      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {resultsLoading && results.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <Spinner size="lg" />
          </div>
        ) : results.length === 0 ? (
          <TableEmptyRow title={t('amlScreeningNoResults')} />
        ) : (
          <>
            <div
              className={`overflow-x-auto${resultsLoading ? ' opacity-60 pointer-events-none' : ''}`}
              style={{ transition: 'opacity 0.15s ease' }}
            >
              <table className="w-full min-w-[980px]">
                <thead>
                  <tr className="border-b border-subtle bg-surface-secondary/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColCustomer')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColAddress')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColResult')}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColScore')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColMatchedName')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColList')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-content-muted uppercase tracking-wider whitespace-nowrap">{t('amlScreeningColReview')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <ResultRow
                      key={result.id}
                      result={result}
                      onOpen={setReviewTarget}
                      t={t}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {resultsTotal > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-subtle bg-surface-secondary/30">
                <PageSizeSelect value={resultsPageSize} onChange={setResultsPageSize} />
                <Pagination
                  page={resultsPage}
                  totalPages={totalPages}
                  onPageChange={fetchResults}
                  totalItems={resultsTotal}
                  startIndex={startItem - 1}
                  endIndex={endItem}
                />
              </div>
            )}
          </>
        )}
      </div>

      <MatchReviewPanel result={reviewTarget} onClose={closeReviewPanel} />
    </div>
  );
};

export default ScreeningRunDetail;
